import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
} from 'react';
import { z } from 'zod';

import { codec, useQueryParams } from '@/hooks/router/use-query-params';

import { AddTaskModal } from './add-task.modal';

export type AddTaskModalOpen =
  | { formType: 'create' | 'link'; workstreamId: string }
  | undefined;

const AddTaskModalContext = createContext<(open: AddTaskModalOpen) => void>(
  () => {
    // This is a no-op function used as the default value for the context
    // It will be overridden by the actual provider
  },
);

export function useAddTaskModal() {
  return useContext(AddTaskModalContext);
}

export function AddTaskModalProvider({ children }: PropsWithChildren<object>) {
  const [{ openTuple }, setParams] = useQueryParams({
    openTuple: {
      key: 'add_task',
      schema: z.tuple([z.enum(['create', 'link']), z.string()]).optional(),
      codec: codec.tuple([codec.string, codec.string], '_'),
    },
  });

  const open: AddTaskModalOpen = openTuple
    ? { formType: openTuple[0], workstreamId: openTuple[1] }
    : undefined;

  const setOpen = useCallback(
    function (open: AddTaskModalOpen) {
      setParams.replace({
        openTuple: open ? [open.formType, open.workstreamId] : undefined,
      });
    },
    [setParams],
  );

  return (
    <AddTaskModalContext.Provider value={setOpen}>
      {children}
      <AddTaskModal open={open} onClose={() => setOpen(undefined)} />
    </AddTaskModalContext.Provider>
  );
}
